import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import {
  UpdateProspectDto,
  updateProspectService,
} from '@hc-frontend/services';

import type { UpdateProspectLandingUrlRepository } from '../../domain';

/**
 * Adapter that upserts a prospect's `landingUrl` via the shared
 * `POST /api/v2/prospects/` endpoint (keyed on `sessionId` + `userId`).
 *
 * `landingPage` is sent alongside `src` — and only when `src` is known —
 * because `createProspect` derives it as `http://{host}/{src}`. Refreshing
 * `src` without it would leave `landing_page` pointing at the previous
 * campaign; in production those two fields currently disagree on 2 leads out of
 * 1,029,383, and this write must not be what breaks that.
 *
 * Note the deliberate difference from `createProspect`, which defaults a missing
 * source to `'ORGANIC'`. This adapter must **never** do that: it updates an
 * existing lead, so defaulting would overwrite a real campaign with `ORGANIC`
 * (the failure mode already seen in production ~100×/day from the create path).
 * When `src` is unknown, both `src` and `landingPage` are omitted and only
 * `landingUrl` is refreshed.
 */
export function updateProspectLandingUrlAdapterFactory({
  baseURL,
}: {
  baseURL: string;
}): UpdateProspectLandingUrlRepository {
  return async (parameters) => {
    try {
      const payload: UpdateProspectDto = {
        userId: parameters.userId,
        sessionId: parameters.sessionId,
        landingUrl: parameters.landingUrl,
        ...(parameters.src
          ? {
              src: parameters.src,
              ...(parameters.host
                ? {
                    landingPage: `http://${parameters.host}/${parameters.src}`,
                  }
                : {}),
            }
          : {}),
      };

      const response = await updateProspectService(baseURL, payload);

      if (response.result !== 'success') {
        return createErrorOperationResult({
          errors: [
            {
              code: 'failedToUpdateProspectLandingUrl',
              rootCause: new Error('Failed to update prospect landing url'),
            },
          ],
        });
      }

      return createSuccessOperationResult(undefined);
    } catch (error) {
      return createErrorOperationResult({
        errors: [
          {
            code: 'failedToUpdateProspectLandingUrl',
            rootCause: error as Error,
          },
        ],
      });
    }
  };
}
